import React, {
  useEffect,
  useState,
  useRef,
} from 'react';
import propTypes from 'prop-types';
import classNames from 'classnames';

import { useAppStore, useWindowStore } from '../../stores';
import { getAuthClient } from '../../utils/auth';
import { buildYourTourApiUrl } from '../../utils/ApiHelpers';
import useDetailViewTracking from '../../hooks/useDetailViewTracking';
import { assemblePlaceDetailNavBtnsData } from './PlaceHelpers';

import FadingContainer from '../FadingContainer/FadingContainer';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

import DetailHeader from '../Detail/DetailHeader';
import PhotoGalleryDetail from '../PhotoGallery/PhotoGalleryDetail/PhotoGalleryDetail';
import VideoDetail from '../Video/VideoDetail';
import VirtualTourDetail from '../VirtualTour/VirtualTourDetail';

import '../Detail/Detail.scss';

const auth = getAuthClient();

const PlaceDetail = ({
  uuid,
  data,
  setModalNavBtns,
  hasInternalHeader,
  navClass,
  parent,
  trackPibViews,
}) => {
  useDetailViewTracking({
    type: 'amenity',
    nid: data?.nid,
    enabled: trackPibViews,
  });

  const [placeData, setPlaceData] = useState(null);
  const [detailHeaderNavBtns, setDetailHeaderNavBtns] = useState(null);
  const [photoGalleryImages, setPhotoGalleryImages] = useState(null);
  const [isContentFaded, setIsContentFaded] = useState(false);

  const isMobile = useWindowStore((state) => state.isMobile);
  const settings = useAppStore((state) => state.settings);
  const showInstructions = settings?.platform !== 'yt';

  // Track component mount status to prevent memory leaks
  const isMountedRef = useRef(true);
  const startedAsNullRef = useRef(!!data || !!uuid);

  // Cleanup on unmount
  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  // If we already have place data, set it (only if not fetching via uuid)
  useEffect(() => {
    if (data && !uuid) {
      setPlaceData(data);
    }
  }, [data, uuid]);

  // Get place data using optimized custom endpoint
  useEffect(() => {
    if (uuid) {
      const fetchData = async () => {
        try {
          const url = buildYourTourApiUrl(`/your_tour_api/place/${uuid}`);
          const rawData = await auth.fetchWithAuthentication(url, 'GET', null);

          // Only update state if component is still mounted
          if (isMountedRef.current && rawData && !rawData.error) {
            setPlaceData(rawData);
          }
        } catch (error) {
          console.error('Error fetching place detail:', error);
        }
      };
      fetchData();
    }
  }, [uuid]);

  // Set navbtns.
  useEffect(() => {
    if (setModalNavBtns && isMountedRef.current) {
      if (!placeData) {
        // Show skeleton while loading
        setModalNavBtns(null);
      } else {
        const rawNavBtns = assemblePlaceDetailNavBtnsData(placeData);
        setModalNavBtns(rawNavBtns);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeData]);

  // Assemble header data if needed.
  // Use undefined when no title exists so DetailHeader doesn't render during skeleton state
  const title = placeData?.title === null || placeData?.title === undefined ? undefined : placeData?.title;
  useEffect(() => {
    if (hasInternalHeader && isMountedRef.current) {
      // Get existing nav passed in or create new nav.
      const rawNavBtns = assemblePlaceDetailNavBtnsData(placeData);
      setDetailHeaderNavBtns(rawNavBtns);
    }
  }, [title,
    hasInternalHeader,
    placeData]);

  // Process photo gallery images or cover photo fallback if set.
  useEffect(() => {
    if (placeData && isMountedRef.current) {
      let images = null;
      if (placeData.photoGalleryImages?.length > 0) {
        images = placeData.photoGalleryImages;
      } else if (
        placeData.coverImage
        && (placeData.threeSixties?.length === 0)
        && !placeData.video
      ) {
        // If there's no vt, video, or gallery, and we want something to appear
        // make the cover photo a "gallery".
        images = [{
          uuid: placeData.coverImage.id,
          caption: null,
          image: placeData.coverImage,
        }];
      }
      setPhotoGalleryImages(images);
    }
  }, [placeData]);

  // Handle fade-in transition if started as null
  useEffect(() => {
    if (startedAsNullRef.current && placeData && isMountedRef.current) {
      // Small delay before fade starts
      const timer = setTimeout(() => {
        setIsContentFaded(true);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [placeData]);

  // No longer need to track modal loading state - we have pre-built nav buttons
  // so scroll can be enabled immediately

  return (
    <FadingContainer>
      <div>

        {hasInternalHeader && (
          <DetailHeader
            title={title}
            navBtns={detailHeaderNavBtns}
            navClassName={navClass}
          />
        )}

        {placeData?.description && (
          <div
            id="about-scroll"
            className="detailSection detailTable"
          >
            <p className="detailTable__description">
              {placeData.description}
            </p>
          </div>
        )}

        {startedAsNullRef.current ? (
          <div className="detailSection--wrapper" style={{ position: 'relative' }}>
            {!isContentFaded && (
              <div className="detailSection detailSection--sixteen9">
                <LoadingSpinner />
              </div>
            )}

            <div
              className={classNames('placeDetail-mediaContent', {
                'placeDetail-mediaContent--fadeIn': isContentFaded,
                'placeDetail-mediaContent--visible': isContentFaded,
              })}
              style={{
                opacity: placeData ? undefined : 0,
                position: !isContentFaded && placeData ? 'absolute' : undefined,
                top: !isContentFaded && placeData ? 0 : undefined,
                left: !isContentFaded && placeData ? 0 : undefined,
                width: !isContentFaded && placeData ? '100%' : undefined,
                pointerEvents: !isContentFaded && placeData ? 'none' : undefined,
              }}
            >
              <PhotoGalleryDetail
                id="slideshow-scroll"
                className={classNames('detailSection detailSection--gallery', {
                  'modal__section--fullWidth': parent === 'modal' && isMobile,
                })}
                images={photoGalleryImages}
                isLoading={placeData === null && !!data?.photoGalleryUuid}
                showInstructions={showInstructions}
                galleryClass="slideshow"
                imageClass="slideshowPhoto"
              />

              <VideoDetail
                id="video-scroll"
                autoplay={false}
                videoId={placeData?.video?.serviceVideoId}
                service={placeData?.video?.service}
                isLoading={placeData === null && !!data?.videoUuid}
                showInstructions={showInstructions}
                className={classNames('detailSection', {
                  'modal__section--fullWidth': parent === 'modal' && isMobile,
                })}
              />

              <VirtualTourDetail
                id="tour-scroll"
                autoplay={false}
                threeSixtiesData={placeData?.threeSixties}
                isLoading={placeData === null && !!data?.virtualTourUuid}
                showInstructions={showInstructions}
                className={classNames('detailSection detailSection--virtualTour detailSection--border', {
                  'modal__section--fullWidth': parent === 'modal' && isMobile,
                })}
              />
            </div>
          </div>
        ) : (
          <>
            <PhotoGalleryDetail
              id="slideshow-scroll"
              images={photoGalleryImages}
              isLoading={placeData === null && !!data?.photoGalleryUuid}
              showInstructions={showInstructions}
              galleryClass={classNames('slideshow detailSection detailSection--gallery', {
                'modal__section--fullWidth': parent === 'modal' && isMobile,
              })}
              imageClass="slideshowPhoto"
            />

            <VideoDetail
              id="video-scroll"
              autoplay={false}
              videoId={placeData?.video?.serviceVideoId}
              service={placeData?.video?.service}
              isLoading={placeData === null && !!data?.videoUuid}
              showInstructions={showInstructions}
              className={classNames('detailSection', {
                'modal__section--fullWidth': parent === 'modal' && isMobile,
              })}
            />

            <VirtualTourDetail
              id="tour-scroll"
              autoplay={false}
              threeSixtiesData={placeData?.threeSixties}
              isLoading={placeData === null && !!data?.virtualTourUuid}
              showInstructions={showInstructions}
              className={classNames('detailSection detailSection--virtualTour detailSection--border', {
                'modal__section--fullWidth': parent === 'modal' && isMobile,
              })}
            />
          </>
        )}

      </div>
    </FadingContainer>
  );
};

PlaceDetail.propTypes = {
  uuid: propTypes.string,
  data: propTypes.shape({
    uuid: propTypes.string,
    title: propTypes.string,
    description: propTypes.string,
    coverImage: propTypes.string,
  }),
  setModalTitle: propTypes.func,
  setModalNavBtns: propTypes.func,
  hasInternalHeader: propTypes.bool,
  navBtns: propTypes.arrayOf(
    propTypes.shape({
      section: propTypes.string,
      text: propTypes.string,
    }),
  ),
  navClass: propTypes.string,
  parent: propTypes.string,
  trackPibViews: propTypes.bool,
};

PlaceDetail.defaultProps = {
  uuid: null,
  data: null,
  setModalNavBtns: null,
  hasInternalHeader: false,
  navBtns: null,
  navClass: null,
  parent: null,
  trackPibViews: false,
};

export default PlaceDetail;
